<template>
  <div class="space-center login">
    <h2 class="login-title noselect">{{ $t('login.title') }}</h2>
    <div class="form">
      <!-- 账号输入 -->
      <div class="input-group" :class="{ 'has-error': accountError }">
        <input ref="accountInputRef" required="true" :placeholder="$t('login.accountPlaceholder')" class="main-input"
          type="text" v-model="formData.account" @input="onAccountInput" @compositionstart="onAccountCompositionStart"
          @compositionend="onAccountCompositionEnd" @blur="onAccountBlur" @change="onAccountBlur" />
        <div class="svg">
          <svg-icon :name="'用户'" color="svg"></svg-icon>
        </div>
        <div v-if="accountError" class="error-message">
          {{ accountError }}
        </div>
      </div>

      <!-- 密码输入 -->
      <div class="input-group password-group" :class="{ 'has-error': passwordError }">
        <input ref="passwordInputRef" required="true" :placeholder="$t('login.passwordPlaceholder')"
          class="main-input shake-line" :type="showPassword ? 'text' : 'password'" v-model="formData.password"
          @compositionstart="onPasswordCompositionStart" @compositionend="onPasswordCompositionEnd" inputmode="text"
          autocorrect="off" autocapitalize="off" @blur="onPasswordBlur" @change="onPasswordBlur" />
        <div class="svg password-toggle" @click="showPassword = !showPassword">
                    <svg-icon :name="showPassword ? '解锁' : '密码'" color="svg" />
        </div>
        <div v-if="passwordError" class="error-message">
          {{ passwordError }}
        </div>
      </div>

      <!-- 图形验证码（登录失败后显示）-->
      <div v-if="showCaptcha" class="input-group captcha-group" :class="{ 'has-error': captchaError }">
        <input ref="captchaInputRef" v-model="formData.captcha" type="text" placeholder="请输入图形验证码" class="main-input"
          maxlength="6" @input="onCaptchaInput" @compositionstart="onCaptchaCompositionStart"
          @compositionend="onCaptchaCompositionEnd" @blur="onCaptchaBlur" @change="onCaptchaBlur" />
        <div class="captcha-img" @click="refreshCaptcha" title="点击刷新">
          <img v-if="captchaImgUrl" :src="captchaImgUrl" alt="验证码" />
          <div v-else class="captcha-placeholder">加载中...</div>
        </div>
        <div v-if="captchaError" class="error-message">
          {{ captchaError }}
        </div>
      </div>

      <!-- 同意协议/忘记密码 -->
      <!-- <div class="group-password">
        <div class="forgot-password" @click="emit('change-mode', 3)">{{ $t('login.forgotPassword') }}</div>
      </div> -->
      <!-- 登录按钮 -->
      <!-- -->
      <!-- 方法1：原生按钮最小改动 + 指针捕获（实现按下锁定） -->
      <button 
        class="Signup-Btn login-button" 
        :class="{ 'is-pressed': isLoginPressed }" 
        :disabled="loading" 
        @pointerdown="onLoginPointerDown" 
        @pointerup="onLoginPointerUp" 
        @pointercancel="onLoginPointerUp" 
        @lostpointercapture="onLoginPointerUp"
        @touchstart.passive="isLoginPressed = true" 
        @touchend.passive="isLoginPressed = false"
        @click="handleSubmit"
      >
        <svg-icon class="icon-loading" v-if="loginBtn == 1" size="18" :name="'加载'" color="#fff" />
        <span class="button-text">
          {{ loginBtn === 1 ? $t('login.loggingIn') : loginBtn === 2 ? $t('login.loginSuccess') :
            $t('login.loginButton') }}
        </span>
      </button>

      <!-- 底部链接：服务设置和协议 -->
      <!-- <div class="bottom-links">
        <span class="link-text server-setting">服务设置</span>
      </div> -->

      <div class="group-password">
        <div class="remember-checkbox">
          <input type="checkbox" v-model="agree" aria-hidden="true" tabindex="-1" />
          <span
            class="checkmark"
            role="checkbox"
            :aria-checked="agree"
            tabindex="0"
            @click.stop="agree = !agree"
            @keydown.enter.prevent="agree = !agree"
            @keydown.space.prevent="agree = !agree"
          ></span>
          <span class="label-text noselect">{{ $t('common.agreement') }} <span class="link noselect"
              @click.stop="showAgreement">{{
                $t('common.userAgreement') }}</span>
            {{ $t('common.and') }}
            <span class="link noselect" @click.stop="showPrivacy">{{ $t('common.privacyPolicy') }}</span>
          </span>
        </div>
      </div>

      <!-- 版本显示入口  @click="router.push('/version-history')"-->
      <!-- <div class="version-display" >
        <span>V {{ currentVersion }}</span>
      </div> -->
      <!-- <div class="version-display" @click="goDownload">
        <span>点击下载 App</span>
      </div> -->
    </div>
    <!-- 全局加载遮罩：登录过程中的 loading 动画 -->
    <MLoading :config="mLoadingConfig" />

    <!-- <dark></dark> -->
    <!-- 其他登录方式 -->
    <div class="login-other" style="display: none;">
      <h5>{{ $t('login.otherLogin') }}</h5>
      <div class="other-icon">
        <SvgIcon size="30" :name="'微信'" color="#07C160" />
        <SvgIcon size="30" :name="'手机'" color="#000000" />
        <SvgIcon size="30" :name="'其他'" color="#999" />
      </div>
    </div>

    <!-- 多租户选择弹窗 -->
    <div v-if="showMoreList" class="tenant-modal" @click="showMoreList = false">
      <div class="tenant-content" @click.stop>
        <h3>{{ $t('login.tenantTitle') }}</h3>
        <div class="tenant-list">
          <div v-for="tenant in moreList" :key="tenant.orgid" class="tenant-item" @click="handleSelect(tenant)">
            {{ tenant.name }}
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useBriefStore, useSettingStore, useUserStore } from 'd3components';
import { toast, isMobile } from 'd3components/src/utils/tools';
import { getKeyStr ,removeKey, setKey, setKeyJsonStr ,getKeyJson,updateKeyJson,getsubKey, removeKeys} from 'd3components/src/utils/localStorage';

import { useI18n } from 'vue-i18n';
import { i18n, setLang } from '@/i18n';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { ApiUser } from 'd3components';
import MLoading from 'd3components/src/components/item-d3/d3/d3_loading.vue';
import mitt from 'mitt-utils';
// import { getPid } from 'actionUtild3components/src/components/actionUtils/';
import { getPid } from 'exeAction-utils';
import { addDynamicRoutes } from '@/router';
import { detectPlatform } from '@/utils/platform'
import { hotUpdateManager } from '@/capacitor/updater/hotupdate';
// import { Browser } from '@capacitor/browser';
import { getJumpviewRouteLocation } from '@/utils/jumpview';

const { iamGetCaptcha } = ApiUser
defineOptions({ name: 'login' });

const router = useRouter();
const route = useRoute();
const { t } = useI18n({ useScope: 'global' });
const userStore = useUserStore();
const settingStore: any = useSettingStore();
const briefStore: any = useBriefStore;

// ===== 输入框引用 =====
const accountInputRef = ref<HTMLInputElement | null>(null);
const passwordInputRef = ref<HTMLInputElement | null>(null);
const captchaInputRef = ref<HTMLInputElement | null>(null);

const isAccountComposing = ref(false);
const isPasswordComposing = ref(false);
const isCaptchaComposing = ref(false);

// ===== 状态管理 =====
const loading = ref(false);
const agree = ref(false);
const agreeUsers = ref<Record<string, boolean>>({});
const accountError = ref('');
const passwordError = ref('');
const captchaError = ref('');
const showPassword = ref(false); // 是否显示密码明文
const showCaptcha = ref(false); // 是否显示图形验证码
const captchaImgUrl = ref(''); // 图形验证码图片（提前加载）
const captchaId = ref(''); // 图形验证码ID
const loginFailCount = ref(0); // 登录失败次数
// 测试下载APP
const goDownload = () => {
  router.push('/download')
}
// 登录页专用的 loading 遮罩配置
const pid = getPid(route);
const mLoadingDid = 'login_m_loading';
const mLoadingMittName = `${pid}_mitt_did_${mLoadingDid}`;
const mLoadingConfig = { did: mLoadingDid, pid };
const openMLoading = async () => {
  await nextTick();
  console.log('login emit open ->', mLoadingMittName);
  mitt.emit(mLoadingMittName, { flg: 'f_open' });
};

const closeMLoading = () => {
  console.log('login emit close ->', mLoadingMittName);
  mitt.emit(mLoadingMittName, { flg: 'f_close' });
};

let hideTimer: any = null;
let showTimer: any = null;

// 表单数据
const formData = ref({
  account: '',
  password: '',
  captcha: '', // 图形验证码
  orgid: null as any,
});

const syncInputValues = () => {
  const account = accountInputRef.value?.value;
  const password = passwordInputRef.value?.value;
  const captcha = captchaInputRef.value?.value;
  if (typeof account === 'string') {
    formData.value.account = account;
  }
  if (typeof password === 'string') {
    formData.value.password = password;
  }
  if (typeof captcha === 'string') {
    formData.value.captcha = captcha;
  }
};

// 多租户
const showMoreList = ref(false);
const moreList = ref<any[]>([]);

// 登录按钮状态
const loginBtn = ref(0);

// 按下锁定状态
const isLoginPressed = ref(false);

/**
 * 登录按钮按下：开启指针捕获，确保拖出按钮也保持按下态
 */
const onLoginPointerDown = (e: PointerEvent) => {
  if (loading.value) return;
  isLoginPressed.value = true;
  const el = e.currentTarget as HTMLElement | null;
  if (el && typeof (el as any).setPointerCapture === 'function') {
    try {
      (el as any).setPointerCapture(e.pointerId);
    } catch (err) {
      console.warn('Pointer capture failed:', err);
    }
  }
};

/**
 * 登录按钮松开/取消：释放状态
 */
const onLoginPointerUp = () => {
  isLoginPressed.value = false;
};

// 当前运行版本
const currentVersion = ref('1.0.0');

const emit = defineEmits(['change-mode']);

defineExpose({
  hasPrivacyConsent: () => agree.value,
});

// ===== 业务逻辑 =====
/**
 *  获取登录历史记录
 * @returns {Array} 登录历史记录数组
 */
const getLoginHistory = () => getsubKey('mobile', 'user_info') as any[];

/**
 *  验证账号输入
 * @returns {boolean} 账号输入是否有效
 */
const validateAccount = () => {
  if (!formData.value.account) {
    accountError.value = t('login.phoneRequired');
    nextTick(() => accountInputRef.value?.focus());
    return false;
  }
  accountError.value = '';
  return true;
};

/**
 *  验证密码输入
 * @returns {boolean} 密码输入是否有效
 */
const validatePassword = () => {
  if (!formData.value.password) {
    passwordError.value = t('login.passwordRequired');
    nextTick(() => passwordInputRef.value?.focus());
    return false;
  }
  if (formData.value.password.length < 2 || formData.value.password.length > 20) {
    passwordError.value = t('login.passwordLength');
    nextTick(() => passwordInputRef.value?.focus());
    return false;
  }
  passwordError.value = '';
  return true;
};

/**
 *  清除表单错误信息
 */
const clearErrors = () => {
  accountError.value = '';
  passwordError.value = '';
  agree.value = false;
};

/**
 *  从登录历史中删除指定账号的用户信息
 * @param {string} account - 要删除的账号
 * @returns {Array} 删除后的登录历史记录
 */
const removeUserFromHistory = (account: string): any[] => {
  try {
    const loginHistory = getLoginHistory();
    if (loginHistory && Array.isArray(loginHistory)) {
      const filteredHistory = loginHistory.filter((record: any) => record.account !== account);
      updateKeyJson('mobile', 'user_info', filteredHistory.slice(0, 10));
      return filteredHistory;
    }
  } catch (e) {
    console.warn('删除用户历史失败:', e);
    return [];
  }
  const latest = getsubKey('mobile', 'user_info');
  return Array.isArray(latest) ? latest : [];
};

/**
 * 清除当前账号信息和相关验证状态，改变了页面风格，之前的删除账号功能没有添加
 */
// const clearAccount = () => {
//   const deletedAccount = formData.value.account;
//   const newHistory = removeUserFromHistory(formData.value.account);
//   agree.value = false;
//   formData.value.account = '';
//   if (newHistory && Array.isArray(newHistory) && newHistory.length > 0) {
//     fillUserInfo(newHistory[0]);
//   }
//   try {
//     const storedAuth: any = getKeyJson('auth');
//     const loginHistory = getLoginHistory();
//     const wasMostRecent = Array.isArray(loginHistory) && loginHistory[0] && loginHistory[0].account === deletedAccount;
//     const authBelongsToDeleted = storedAuth && (storedAuth.account === deletedAccount || storedAuth.userAccount === deletedAccount);
//     if (storedAuth && (wasMostRecent || authBelongsToDeleted)) {
//       removeKey('auth');
//     }
//   } catch (e) {
//     console.warn('清理 auth 失败:', e);
//   }
//   clearErrors();
// };

/**
 * 处理账号输入事件
 *
 */
const onAccountInput = (e: Event) => {
  if (isAccountComposing.value) {
    return;
  }
  clearErrors();
  const target = e.target as HTMLInputElement;
  const raw = target?.value ?? '';
  formData.value.account = raw;
  try {
    const loginHistory = getLoginHistory();
    const accountKey = raw.trim();
    const rec = loginHistory?.find((r: any) => r.account === accountKey);
    if (rec && typeof rec.agreeStatus !== 'undefined') {
      agree.value = !!rec.agreeStatus;
    } else if (accountKey && agreeUsers.value[accountKey]) {
      agree.value = true;
    } else if (!accountKey) {
      agree.value = false;
    }
  } catch (e) {
    const accountKey = raw.trim();
    if (accountKey && agreeUsers.value[accountKey]) {
      agree.value = true;
    } else if (!accountKey) {
      agree.value = false;
    }
  }
};

const onAccountCompositionStart = () => {
  isAccountComposing.value = true;
};

const onAccountCompositionEnd = (e: Event) => {
  isAccountComposing.value = false;
  onAccountInput(e);
};

const onAccountBlur = (e: Event) => {
  // 兜底：部分 App/安全键盘只改 DOM 值但不触发 input，此处强制同步
  isAccountComposing.value = false;
  const target = e.target as HTMLInputElement;
  const raw = target?.value ?? '';
  formData.value.account = raw;
  onAccountInput(e);
};

const onPasswordCompositionStart = () => {
  isPasswordComposing.value = true;
};

const onPasswordCompositionEnd = (e: Event) => {
  isPasswordComposing.value = false;
  const target = e.target as HTMLInputElement;
  formData.value.password = target?.value ?? '';
};

const onPasswordBlur = (e: Event) => {
  // 兜底：失焦时强制把 DOM 最终值同步到 v-model，避免渲染覆盖清空
  isPasswordComposing.value = false;
  const target = e.target as HTMLInputElement;
  formData.value.password = target?.value ?? '';
};

const onCaptchaInput = (e: Event) => {
  if (isCaptchaComposing.value) {
    return;
  }
  const target = e.target as HTMLInputElement;
  formData.value.captcha = target?.value ?? '';
  captchaError.value = '';
};

const onCaptchaCompositionStart = () => {
  isCaptchaComposing.value = true;
};

const onCaptchaCompositionEnd = (e: Event) => {
  isCaptchaComposing.value = false;
  onCaptchaInput(e);
};

const onCaptchaBlur = (e: Event) => {
  isCaptchaComposing.value = false;
  const target = e.target as HTMLInputElement;
  formData.value.captcha = target?.value ?? '';
  captchaError.value = '';
};
/**
 *  填充用户信息到表单
 */
const fillUserInfo = (user: any) => {
  formData.value.account = user.account;
  const agreeStatus = user.agreeStatus || agreeUsers.value[user.account] || false;
  nextTick(() => {
    agree.value = agreeStatus;
  });
  if (user.lang) {
    setLang(user.lang);
  }
};

/**
 *  初始化记住账号功能，从本地存储恢复账号信息
 */
const initRememberAccount = () => {
  const loginHistory = getLoginHistory();
  if (!loginHistory || !Array.isArray(loginHistory) || loginHistory.length === 0) {
    const savedAccount = getKeyStr('lgusr');
    if (savedAccount) {
      formData.value.account = savedAccount;
    }
    if (savedAccount && agreeUsers.value[savedAccount]) {
      agree.value = true;
    }
    return;
  }
  fillUserInfo(loginHistory[0]);
};

/**
 *  加载服务配置
 * @param {string} ctxt - 上下文配置信息
 */
/**
 *  处理租户选择事件
 * @param {Object} action - 租户信息对象
 */
const handleSelect = (action: any) => {
  formData.value.orgid = action.orgid;
  showMoreList.value = false;
  handleSubmit();
};

/**
 *  提交登录表单
 */
const handleSubmit = async () => {
  if (!validateAccount() || !validatePassword() || !validateCaptcha()) {
    return;
  }
  if (!agree.value) {
    toast(t('common.agreementRequired'), 3);
    return;
  }

  loading.value = true;
  loginBtn.value = 1;
  await openMLoading();

  const submitData: any = {
    ...formData.value,
    account: formData.value.account.trim(),
    password: formData.value.password.trim(),
  };

  // 如果显示了验证码，则添加验证码参数
  if (showCaptcha.value) {
    submitData.captchaId = captchaId.value
    submitData.captchaCode = formData.value.captcha
  }
    if (detectPlatform() == 1) {
    submitData.clientId = 'wx'
  } else if (detectPlatform() == 2) {
    submitData.clientId = 'app'
  }else{
    // 移动版默认走wx方便调试
    submitData.clientId = 'wx'
  }
  Promise.race([userStore.webLogin(submitData), new Promise((_, r) => setTimeout(r, 5000, '请求超时'))])
    .then(async () => {
      setKey('login',JSON.stringify({ctxt: getsubKey('service','ctxt'),path: "/tenantlogin"}))
      loginFailCount.value = 0
      showCaptcha.value = false
      formData.value.captcha = ''

      const finalLang = i18n.global?.locale?.value ?? 'zh-cn';
      const loginUnid = userStore.unid;

      if (loginUnid) {
        const history = Array.isArray(getsubKey('mobile', 'user_info')) ? [...getsubKey('mobile', 'user_info')] : [];
        const userIdx = history.findIndex((r: any) => r.unid === loginUnid);
        const prevRecord = userIdx >= 0 ? history[userIdx] : { unid: loginUnid };
        const nextRecord: any = {
          ...prevRecord,
          account: formData.value.account.trim(),
          loginTime: new Date().toISOString(),
          lang: finalLang,
          agreeStatus: agree.value,
          unid: loginUnid,
        };
        if (userIdx >= 0) history.splice(userIdx, 1);
        history.unshift(nextRecord);
        updateKeyJson('mobile', 'user_info', history.slice(0, 10));
      }

      if (agree.value && submitData.account) {
        agreeUsers.value[submitData.account] = true;
      }

      try {
        await userStore.firstMenuFun();
         await addDynamicRoutes(true)
        await serviceConfig(userStore.ctxt);
        initTrueTheme();

        if (isMobile()) {
          settingStore['layout'] = 'drawer';
          settingStore['isUseTabsRouter'] = false;
          settingStore['isSidebarCompact'] = false;
        }

        await briefStore.getIamBrief();
        await briefStore.getServiceBrief();

         const targetRoute = getJumpviewRouteLocation(router, route.query)
        console.log('[default 租户] 登录后目标路由:', targetRoute || '/')
        router.replace(targetRoute || '/')
      } catch (e: any) {
        console.error('登录后初始化失败', e);
        toast(e?.msg || String(e), 3);
      }
    })
    .catch((err: any) => {
      // 重置登录按钮状态
      loginBtn.value = 0;
      loading.value = false;

      if (err === '请求超时') {
        toast(err, 3);
        return;
      }
      delete (formData.value as any)['orgid'];
      if (err && err.res === 'f_more') {
        // f_more 不算登录失败，只是弹出公司选择，验证码不刷新
        moreList.value = err.arr.map((item: any) => ({
          ...item,
          name: item.orgname,
        }));
        showMoreList.value = true;
      } else {
        // 真正的登录失败才刷新验证码
        if (!showCaptcha.value) {
          showCaptcha.value = true
          refreshCaptcha()
        } else {
          refreshCaptcha()
          formData.value.captcha = ''
        }
        toast(err, 3); // 登录失败
        console.log('登录失败:', err);
      }
    })
    .finally(() => {
      // 确保无论如何都重置状态
      loading.value = false;
      if (loginBtn.value === 1) {
        loginBtn.value = 0;
      }
      closeMLoading();
    });
};

const serviceConfig = async (ctxt: string) => {
  if (!ctxt) {
    return;
  }
  try {
    await userStore.loadServiceConfig(ctxt);
  } catch (e: any) {
    if (e?.response && e?.response?.status > 0) {
      toast(`${e?.config?.url}：${e?.response?.status}-${e?.response?.statusText}`, 3);
    } else {
      toast(e?.message || String(e), 3);
    }
    throw e;
  }
};

function initTrueTheme() {
  try {
    settingStore.updateConfig(userStore['erpConfig']);
  } catch (e) {
    console.log('初始化合并异常', e);
  }
}

/**
 *  打开用户协议页面
 */
const showAgreement = () => {
  // 使用路由跳转，保持历史记录，方便返回到登录页
  // router.push('/protocol')
  // router.push('/default-protocol')
   window.open('https://www.d3inf.com/app/protocol.html')
}

/**
 *  打开隐私政策页面
 */
const showPrivacy = async () => {
  window.open('https://www.d3inf.com/app/privacy.html')
};

/**
 * 刷新图形验证码
 */
const refreshCaptcha = async () => {
  // 如果没有输入账号，不请求验证码
  if (!formData.value.account) {
    return
  }
  const res = await iamGetCaptcha(formData.value.account.trim())
    if (res && res.code == 0) {
      captchaImgUrl.value = res.value.img
      captchaId.value = res.value.id || res.value.captcha_id || res.value.captchaId || ''
    } else {
      toast(res?.msg || '获取验证码失败', 3)
    }
  // try {
  //   const res = await iamGetCaptcha(formData.value.account.trim())
  //   if (res && res.code == -1 && res.isSuc) {
  //     captchaImgUrl.value = res.obj.img
  //     captchaId.value = res.obj.id || res.obj.captcha_id || res.obj.captchaId || ''
  //   } else {
  //     toast(res?.msg || '获取验证码失败', 3)
  //   }
  // } catch (err: any) {
  //   toast(err?.message || '获取验证码异常', 3)
  // }
}

/**
 * 验证图形验证码
 */
const validateCaptcha = () => {
  if (showCaptcha.value && !formData.value.captcha) {
    captchaError.value = '请输入图形验证码'
    nextTick(() => captchaInputRef.value?.focus())
    return false
  }
  captchaError.value = ''
  return true
}


// 生命周期
onMounted(async () => {
  initRememberAccount();

  // 获取当前显示版本
  try {
    currentVersion.value = await hotUpdateManager.getDisplayedVersion();
  } catch (e) {
    console.warn('获取版本失败', e);
  }

  // 注意：默认租户的验证码需要账号参数，所以不在这里提前加载
  // 验证码会在登录失败时才加载

  // ===== 自动聚焦第一个输入框 =====
  nextTick(() => {
    accountInputRef.value?.focus();
  });

  // 只在原生平台上监听键盘事件
  if (Capacitor.isNativePlatform()) {
    Keyboard.addListener('keyboardWillShow', () => {
      clearTimeout(showTimer);
      document.body.classList.add('keyboard-open');
    });
    Keyboard.addListener('keyboardWillHide', () => {
      clearTimeout(hideTimer);
      showTimer = setTimeout(() => {
        document.body.classList.remove('keyboard-open');
      }, 250);
    });
    Keyboard.addListener('keyboardDidHide', () => {
      syncInputValues();
    });
  }
});

onUnmounted(() => {
  clearTimeout(hideTimer);
  clearTimeout(showTimer);
  if (Capacitor.isNativePlatform()) {
    Keyboard.removeAllListeners();
  }
});
</script>

<style scoped lang="scss">
.page-loading-mask {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.35);
}

.page-loading-mask :deep(view) {
  display: block;
}

.page-loading-mask :deep(.wrapper) {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.page-loading-mask :deep(.spinner) {
  margin: 0;
}

// ===== 科技感配色 =====
//$primary-gradient: linear-gradient(135deg, rgba(57, 140, 254, 1) 0%, rgba(26, 111, 220, 1) 100%);
// $primary-gradient: var(--color-primary);
$accent-color: rgba(57, 140, 254, 1);
// $accent-color: var(--color-primary);
$primary-gradient: rgba(57, 140, 254, 1);
// $primary-gradient: $primary-color-login-text;
//$accent-color: ;
$accent-color: $primary-gradient;
$light-accent: rgba(57, 140, 254, 0.1);
$text-secondary: #8a94a6;
$border-light: rgba(57, 140, 254, 0.2);

// ===== 间距变量 =====
$spacing-xs: 0.8rem;
$spacing-sm: 0.85rem;
$spacing-md: 1.25rem;
$spacing-lg: 1.5rem;
$spacing-xl: 1.875rem;
$spacing-2xl: 2.5rem;
$spacing-3xl: 3rem;

// ===== 圆角变量 =====
$radius-sm: 4px;
$radius-md: 8px;
$radius-lg: 14px;
$radius-xl: 16px;
$radius-full: 54px;
$radius-circle: 50%;

// ===== 阴影变量 =====
$shadow-xs: 0 2px 8px rgba(0, 0, 0, 0.02);
$shadow-sm: 0 2px 10px rgba(57, 140, 254, 0.3);
$shadow-md: 0 4px 16px rgba(57, 140, 254, 0.3);
$shadow-lg: 0 6px 20px rgba(57, 140, 254, 0.45);
$shadow-hover: 0 4px 16px rgba($accent-color, 0.08);
$shadow-focus: 0 0 0 4px rgba($accent-color, 0.1),
  0 6px 20px rgba($accent-color, 0.12);
$shadow-modal: 0 10px 40px rgba(0, 0, 0, 0.2);

// ===== 字体变量 =====
$font-size-sm: 0.8rem;
$font-size-base: 0.95rem;
$font-size-lg: 1rem;
$font-size-xl: 1.1rem;
$font-size-2xl: 28px;
$font-weight-medium: 500;
$font-weight-semibold: 600;
$font-weight-bold: 900;

// ===== 动画变量 =====
$transition-fast: 0.15s ease;
$transition-base: 0.3s ease;
$transition-md: 0.35s cubic-bezier(0.4, 0, 0.2, 1);
$transition-lg: 0.4s cubic-bezier(0.4, 0, 0.2, 1);
$transition-xl: 0.5s ease;

.noselect {
  user-select: none;
  -webkit-user-select: none;
  /* iOS/老安卓 */
  -webkit-touch-callout: none;
  /* iOS 长按菜单 */
  /* Standard syntax */
}

.login {
  width: 100%;

  // ===== 标题动画样式 =====
  h2 {
    background: $primary-gradient;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    letter-spacing: 0.1em;
  }

  // 新增登录标题类样式
  .login-title {
    font-weight: $font-weight-bold;
    margin-bottom: 1.3rem;
    user-select: none;
  }

  // ===== 表单容器增强 =====
  :deep(.form) {
    padding: $spacing-lg 0;
    width: 80%;

    // 输入框组 - 现代卡片式设计
    .input-group {
      position: relative;
      margin-top: $spacing-xl;
      background: #fafbfc;
      border: 2px solid #e8ecf2 !important;
      border-radius: $radius-full;
      padding: 20px 54px;
      transition: all $transition-md;
      box-shadow: $shadow-xs;


      // 悬浮效果
      &:hover {
        border-color: rgba($accent-color, 0.4);
        background: #fff;
        box-shadow: $shadow-hover;
      }

      // 聚焦效果
      &:focus-within {
        border-color: $accent-color !important;
        background: #fff;
        box-shadow: $shadow-focus;

        .lebal-tilte {
          color: $accent-color !important;
          font-weight: $font-weight-semibold;
        }

        .svg {
          opacity: 1;
          color: $accent-color;
          // transform: scale(1.15);
        }
      }

      // 错误状态样式
      &.has-error {
        border-color: #f56c6c !important;
        background: #fef0f0;

        &:hover {
          border-color: #f56c6c !important;
          background: #fef0f0;
        }

        &:focus-within {
          border-color: #f56c6c !important;
          background: #fef0f0;
          box-shadow: 0 0 0 4px rgba(245, 108, 108, 0.1);

          .svg {
            color: #f56c6c;
          }
        }

        .svg {
          color: #f56c6c;
        }
      }

      // 错误提示文字（绝对定位，不占据高度）
      .error-message {
        position: absolute;
        left: 54px;
        right: 54px;
        top: 100%;
        margin-top: 8px;
        font-size: 24px;
        color: #f56c6c;
        line-height: 1.2;
        font-weight: 500;
        animation: slideDown 0.3s ease;
        z-index: 10;
      }

      // 输入框本身
      .main-input {
        padding: 0.5rem 0.5rem 0.5rem 0.25rem;
        border: none;
        background: transparent !important;
        font-size: $font-size-lg;
        font-weight: $font-weight-medium;
        color: #2d3748;
        width: 100%;
        transition: all $transition-base;

        &::placeholder {
          color: $text-secondary;
        }

        &:focus {
          outline: none;
        }
      }

      // 浮动标签
      .lebal-tilte {
        position: absolute;
        left: 0.85rem;
        top: 50%;
        transform: translateY(-50%);
        font-size: $font-size-base;
        color: #9ca3af;
        font-weight: $font-weight-medium;
        pointer-events: none;
        transition: all $transition-md;
        background: transparent;
        padding: 0 0.25rem;
      }

      // 右侧图标
      .svg {
        position: absolute;
        right: 0.9rem;
        top: 45%;
        transform: translateY(-50%);
        font-size: 1.5rem;
        // color: #b0b8c4;
        color: $accent-color;
        opacity: 0.7;
        transition: all $transition-md;

        // 密码切换图标可点击
        &.password-toggle {
          cursor: pointer;
          user-select: none;

          &:hover {
            opacity: 1;
            transform: translateY(-50%) scale(1.1);
          }

          &:active {
            transform: translateY(-50%) scale(0.95);
          }
        }
      }

      // 隐藏原有的highlight-span
      .highlight-span {
        display: none;
      }
    }

    // 新增密码组类样式
    .password-group {
      margin-top: $spacing-lg;
    }

    // 图形验证码行：input 占左侧，验证码图片在右侧
    .captcha-group {
      padding-right: 0;
      display: flex;
      align-items: center;

      .main-input {
        flex: 1;
        padding-right: 0.5rem;
      }

      .captcha-img {
        flex-shrink: 0;
        width: 180px;
        height: 100%;
        cursor: pointer;
        border-left: 2px solid #e8ecf2;
        display: flex;
        margin-right: 20px;
        align-items: center;
        justify-content: center;
        border-radius: 0 $radius-full $radius-full 0;
        transition: opacity $transition-base;

        &:hover {
          opacity: 0.8;
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .captcha-placeholder {
          font-family: 'Courier New', monospace;
          font-size: 1.5rem;
          font-weight: $font-weight-bold;
          letter-spacing: 0.25em;
          // 彩色渐变文字
          background: linear-gradient(120deg, #3a7bd5 0%, #e96c5a 40%, #3a7bd5 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          // 背景：网格噪点质感
          background-color: #eef5ff;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          user-select: none;
          position: relative;
          // 模拟噪点纹理
          background-image:
            linear-gradient(120deg, #3a7bd5 0%, #e96c5a 40%, #3a7bd5 100%),
            repeating-linear-gradient(45deg,
              rgba(57, 123, 213, 0.04) 0px,
              rgba(57, 123, 213, 0.04) 1px,
              transparent 1px,
              transparent 8px);
          text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.6), -1px -1px 2px rgba(0, 0, 0, 0.08);
        }
      }
    }

    // 第二个输入框间距调整
    .input-group+.input-group,
    .input-group[style*='margin-top'] {
      margin-top: $spacing-xl !important;
    }

    // 密码操作区域
    .group-password {
      margin-top: $spacing-lg;
      padding: 0 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      // 圆形 checkbox 样式
      .remember-checkbox {
        display: flex;
        align-items: center;
        cursor: default;
        user-select: none;

        input[type='checkbox'] {
          display: none;
        }

        .checkmark {
          width: 24px;
          height: 24px;
          display: inline-block;
          min-width: 24px;
          border: 2px solid #d1d5db;
          border-radius: $radius-sm;
          margin-right: 8px;
          position: relative;
          transition: all $transition-base;
          background: #fff;
          cursor: pointer;

          &::after {
            content: '✓';
            position: absolute;
            top: 50%;
            left: 50%;
            font-size: 12px;
            font-weight: bold;
            color: #fff;
            transform: translate(-50%, -50%) scale(0);
            transition: transform 0.2s ease;
          }
        }

        input[type='checkbox']:checked+.checkmark {
          border-color: $accent-color;
          background: $accent-color;

          &::after {
            transform: translate(-50%, -50%) scale(1);
          }
        }

        .label-text {
          font-size: 0.85rem;
          color: $text-secondary;
          font-weight: $font-weight-medium;

          // line-height: 1.5;
          .link {
            color: $accent-color;
            font-weight: 600;
            cursor: pointer;
          }
        }
      }

      .forgot-password {
        font-size: $font-size-sm;
        color: $accent-color;
        font-weight: $font-weight-medium;
        cursor: pointer;
        transition: opacity $transition-base;

        &:hover {
          opacity: 0.8;
        }
      }
    }

    // 协议链接样式
    .agreement {
      margin-top: $spacing-xs;
      font-size: $font-size-sm;
      color: $text-secondary;
      text-align: center;

      .link-text {
        color: $accent-color;
        font-weight: $font-weight-semibold;
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    // 版本显示样式
    .version-display {
      margin-top: 1.5rem;
      text-align: center;
      cursor: pointer;
      opacity: 0.6;

      span {
        font-size: 0.8rem;
        color: var(--color-text-secondary, #94a3b8);
        text-decoration: underline;
      }

      &:active {
        opacity: 1;
      }
    }

    // 登录按钮增强
    .Signup-Btn {
      touch-action: none; /* 关键：禁止浏览器拦截触控手势，确保 pointerdown 100% 触发 */
      // -webkit-tap-highlight-color: transparent; /* 去掉移动端默认点击高亮蓝框 */
      user-select: none; /* 防止长按选中文本 */

      // 从内联样式移过来的margin-top属性
      &.login-button {
        margin-top: $spacing-xl; // 转换30px为rem
      }

      background: $primary-gradient;
      border: none;
      padding: 0 $spacing-lg;
      height: 88px;
      min-height: 88px;
      font-size: $font-size-lg;
      font-weight: $font-weight-semibold;
      letter-spacing: 0.15em;
      box-shadow: $shadow-md;
      transition: all $transition-lg;
      position: relative;
      overflow: hidden;
      border-radius: $radius-full;
      color: #fff;
      width: 100%;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left $transition-xl;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: $shadow-lg;

        &::before {
          left: 100%;
        }
      }

      // &:active {
      //   transform: translateY(0) scale(0.98);
      //   box-shadow: $shadow-sm;
      // }
      &:active,
&.is-pressed {
  transform: translateY(0) scale(0.98) !important;
  box-shadow: $shadow-sm !important;
  transition: transform 0.1s; /* 按下时反馈更灵敏 */
}
      // 按钮文本样式
      .button-text {
        margin-left: 10px;
      }
    }
  }

  // ===== 其他登录方式 =====
  .login-other {
    width: 80%;
    margin-top: $spacing-2xl;
    color: $text-secondary;

    &>h5 {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: $spacing-lg;
      color: $text-secondary;
      font-weight: $font-weight-medium;
      font-size: $font-size-2xl;
      text-align: center;
      white-space: nowrap;
      letter-spacing: 0.05em;

      &::before,
      &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, transparent, $border-light, transparent);
      }

      &::before {
        margin-right: $spacing-xs;
        background: linear-gradient(90deg, transparent, $border-light);
      }

      &::after {
        margin-left: $spacing-xs;
        background: linear-gradient(90deg, $border-light, transparent);
      }
    }

    .other-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: $spacing-3xl;
      perspective: 500px;

      &>.icon {
        cursor: pointer;
        padding: $spacing-sm;
        border-radius: $radius-circle;
        transition: all $transition-fast;
        position: relative;

        background-image: linear-gradient(to top, #d8d9db 0%, #fff 80%, #fdfdfd 100%);
        border-radius: 75px;
        border: 1px solid #ddd;
        text-shadow: 0 1px #fff;

        &::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 60%;
          height: 6px;
          background: radial-gradient(ellipse, rgba(0, 0, 0, 0.15) 0%, transparent 70%);
          border-radius: $radius-circle;
          transition: all $transition-fast;
        }

        &:hover {
          transform: scale(1.1);
          box-shadow:
            8px 12px 20px rgba(57, 140, 254, 0.25),
            -4px -4px 12px rgba(255, 255, 255, 1),
            inset 1px 1px 3px rgba(255, 255, 255, 0.9),
            inset -1px -1px 3px rgba(57, 140, 254, 0.1);

          &::after {
            width: 50%;
            opacity: 0.8;
            bottom: -12px;
          }
        }

        &:active {
          transform: translateY(-2px) scale(1.05);
          box-shadow:
            4px 4px 8px rgba(0, 0, 0, 0.12),
            -2px -2px 6px rgba(255, 255, 255, 0.8),
            inset 2px 2px 4px rgba(0, 0, 0, 0.08),
            inset -1px -1px 2px rgba(255, 255, 255, 0.6);
        }
      }
    }
  }

  // ===== 多租户弹窗样式 =====
  .tenant-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;

    .tenant-content {
      background: #fff;
      border-radius: $radius-xl;
      padding: $spacing-lg;
      min-width: 280px;
      max-width: 90%;
      box-shadow: $shadow-modal;

      h3 {
        margin: 0 0 $spacing-xs;
        font-size: $font-size-xl;
        color: #2d3748;
        text-align: center;
      }

      .tenant-list {
        max-height: 300px;
        overflow-y: auto;

        .tenant-item {
          padding: 0.75rem $spacing-xs;
          border-radius: $radius-md;
          cursor: pointer;
          transition: background $transition-base;

          &:hover {
            background: rgba($accent-color, 0.1);
          }

          &:active {
            background: rgba($accent-color, 0.2);
          }
        }
      }
    }
  }
}

// ===== 关键帧动画 =====
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes lineExpand {
  from {
    width: 0;
  }

  to {
    width: 40px;
  }
}

// ===== 输入框焦点波纹效果 =====
@keyframes focusRipple {
  0% {
    transform: scale(0);
    opacity: 0.5;
  }

  100% {
    transform: scale(2);
    opacity: 0;
  }
}

// ===== 浮动动画（装饰用）=====
@keyframes float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-6px);
  }
}

// ===== 错误提示滑入动画 =====
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
